import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { useContext, useEffect, useMemo, useState } from "react";
import { ModalContext } from "../../../../Components/Modals";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { dataService } from "../../../../Core/Api/services";
import { DbDeliveryMetadataExportInfos } from "../../../../Core/Api/Types/db-delivery-metadata-export-infos";
import AddIcon from "@mui/icons-material/Add";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ModalCreateOrUpdateExportSession, {
    ModalCreateOrUpdateExportSessionElementProps,
} from "../../../../Components/Modals/ModalCreateOrUpdateExportSession/modal-create-or-update-export-session";
import { Button, Stack } from "@mui/material";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DeliveryMetadataExportType } from "../../../../Core/Api/Types/delivery-metadata-export-type";
import { useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsExportSessions() {
    const { t } = useTranslation();
    // context
    const modalContext = useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const [isLoading, setIsLoading] = useState(false);

    const enqueueSnackbar = useCustomSnackbar();
    const navigate = useNavigate();

    // #region States
    const [metaExports, setMetaExports] = useState<DbDeliveryMetadataExportInfos[]>([]);
    // #endregion States

    const handleCreateClick = () => {
        openModal &&
            openModal<ModalCreateOrUpdateExportSessionElementProps>(ModalCreateOrUpdateExportSession, {
                mode: "create",
            });
    };

    const handleEditClick = (id: number) => {
        console.log(metaExports.find((x) => x.id == id));
        openModal &&
            openModal<ModalCreateOrUpdateExportSessionElementProps>(ModalCreateOrUpdateExportSession, {
                mode: "update",
                object: metaExports.find((x) => x.id == id),
            });
    };

    const handleEditLiquid = (id: number) => {
        const metaExport = metaExports.find((x) => x.id === id);
        if (
            metaExport &&
            (metaExport.type === DeliveryMetadataExportType.LiquidTransform ||
                metaExport.type === DeliveryMetadataExportType.ExcelPatcher)
        ) {
            navigate(`${routePaths.settingsExportSessionsLiquid.path}/${id}`);
        }
    };

    const deleteMetadataExport = (id: number) => {
        dataService.DeleteMetadataExport(id).then((response) => {
            if (response && response.success) enqueueSnackbar(t("alert:success.metadataExportDeleted"), "success");
            else enqueueSnackbar(t("alert:error.deletingMetadataExport", { message: response.message }), "error");
        });
    };

    const handleDeleteClick = (id: number) => {
        openModal &&
            openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                title: t("modal:title.confirmMetadataExportDeletion"),
                displayComment: false,
                onConfirm: () => deleteMetadataExport(id),
            });
    };

    useEffect(() => {
        setIsLoading(true);
        dataService.GetMetadataExports().then((response) => {
            if (response.exports) {
                setMetaExports(response.exports);
            }
            setIsLoading(false);
        });
    }, []);

    const tableColumns: GridColDef[] = useMemo(
        () => [
            {
                headerName: "Id",
                field: "id",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
            },
            {
                headerName: t("common:name"),
                field: "name",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
            },
            {
                headerName: t("common:description"),
                field: "description",
                flex: CellSize.small,
                minWidth: CellSize.small,
            },
            {
                headerName: t("common:origin"),
                field: "origin",
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: any) => <TableCellChip data={data} />,
            },
            {
                headerName: t("common:type"),
                field: "type",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: any) => (
                    <TableCellChip
                        data={data}
                        color={data.value === DeliveryMetadataExportType.LiquidTransform ? "info" : "default"}
                    />
                ),
            },
            {
                headerName: t("common:xsltOrExcelPath"),
                field: "xslFilePath",
                flex: CellSize.large,
                minWidth: CellSize.large,
            },
            {
                headerName: "",
                field: "actions",
                type: "actions",
                align: "right",
                flex: CellSize.actionsSmall,
                minWidth: CellSize.actionsSmall,
                getActions: (params: GridRowParams) => {
                    if (!params || !params.row) return;
                    const actions = [
                        <GridActionsCellItem
                            icon={<EditOutlinedIcon />}
                            label={t("common:editUpperCase")}
                            onClick={() => handleEditClick(params.row["id"])}
                        />,
                        <GridActionsCellItem
                            icon={<DeleteOutlineOutlinedIcon />}
                            label={t("common:delete")}
                            color="error"
                            onClick={() => handleDeleteClick(params.row["id"])}
                        />,
                    ];
                    if (
                        (params.row["type"] as DeliveryMetadataExportType) === DeliveryMetadataExportType.LiquidTransform ||
                        (params.row["type"] as DeliveryMetadataExportType) === DeliveryMetadataExportType.ExcelPatcher
                    )
                        actions.splice(
                            0,
                            0,
                            <GridActionsCellItem
                                icon={<CodeOutlinedIcon />}
                                label={t("table:cellLabel.editLiquidCode")}
                                onClick={() => handleEditLiquid(params.row["id"])}
                            />
                        );
                    return actions;
                },
            },
        ],
        [metaExports]
    );

    const additionalActions = (
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateClick}>
            {t("button:newExport")}
        </Button>
    );

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_EXPORT_SESSIONS}
            additionalActions={additionalActions}
            columns={tableColumns}
            rows={metaExports}
            getRowHeight={() => "auto"}
            pagination={true}
        />
    );
}

export default AdminSettingsExportSessions;
